import type { SoftphoneRealtimeMessage } from '@/types/api/softphone'

export const SOFTPHONE_REALTIME_SOCKET_ERROR_MESSAGE =
  'Softphone realtime socket error'
export const SOFTPHONE_REALTIME_AUTH_ERROR_MESSAGE =
  'Softphone realtime authentication failed'

export type SoftphoneRealtimeStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'error'
  | 'unavailable'

interface SoftphoneRealtimeAuth {
  accessToken: string
  cognitoIssuer: string
}

interface SoftphoneRealtimeSocketOptions {
  websocketUrl?: string
  accessToken?: string | null
  cognitoIssuer?: string | null
  getAuth?: () => Promise<SoftphoneRealtimeAuth>
  dispatcherId?: string
  maxReconnectAttempts?: number
  onStatusChange?: (status: SoftphoneRealtimeStatus) => void
  onMessage?: (message: SoftphoneRealtimeMessage) => void
  onError?: (error: Error) => void
}

const DEFAULT_MAX_RECONNECT_ATTEMPTS = 8

const parseSocketMessage = (value: unknown): SoftphoneRealtimeMessage => {
  if (typeof value !== 'string') return {}

  try {
    const parsed = JSON.parse(value) as SoftphoneRealtimeMessage

    if (typeof parsed.data === 'string') {
      try {
        return {
          ...parsed,
          data: JSON.parse(parsed.data),
        }
      } catch {
        return parsed
      }
    }

    return parsed
  } catch {
    return {}
  }
}

export class SoftphoneRealtimeSocket {
  private socket: WebSocket | null = null
  private reconnectTimer: number | null = null
  private reconnectAttempt = 0
  private intentionallyClosed = false
  private opened = false
  private connectGeneration = 0

  constructor(private readonly options: SoftphoneRealtimeSocketOptions) {}

  connect(): void {
    const websocketUrl = this.options.websocketUrl
    if (!websocketUrl) {
      this.options.onStatusChange?.('unavailable')
      return
    }

    this.intentionallyClosed = false
    this.opened = false
    this.clearReconnectTimer()
    this.options.onStatusChange?.('connecting')
    const generation = ++this.connectGeneration
    void this.connectWithCurrentAuth(generation, websocketUrl)
  }

  private async connectWithCurrentAuth(
    generation: number,
    websocketUrl: string
  ): Promise<void> {
    try {
      const auth = this.options.getAuth
        ? await this.options.getAuth()
        : {
            accessToken: this.options.accessToken || '',
            cognitoIssuer: this.options.cognitoIssuer || '',
          }

      if (generation !== this.connectGeneration || this.intentionallyClosed) {
        return
      }

      if (!auth.accessToken || !auth.cognitoIssuer) {
        this.options.onStatusChange?.('unavailable')
        this.options.onError?.(new Error(SOFTPHONE_REALTIME_AUTH_ERROR_MESSAGE))
        return
      }

      const url = new URL(websocketUrl)
      url.searchParams.set('access_token', auth.accessToken)
      url.searchParams.set('cognito_issuer', auth.cognitoIssuer)

      const socket = new WebSocket(url.toString())
      this.socket = socket

      socket.onopen = () => {
        if (this.socket !== socket) return
        this.opened = true
        this.reconnectAttempt = 0
        this.options.onStatusChange?.('connected')
        this.registerDispatcher()
      }
      socket.onmessage = event => {
        if (this.socket !== socket) return
        this.options.onMessage?.(parseSocketMessage(event.data))
      }
      socket.onerror = () => {
        if (this.socket !== socket) return
        this.options.onStatusChange?.('error')
        this.options.onError?.(
          new Error(SOFTPHONE_REALTIME_SOCKET_ERROR_MESSAGE)
        )
      }
      socket.onclose = event => {
        if (this.socket !== socket) return
        this.socket = null
        if (this.intentionallyClosed) {
          this.options.onStatusChange?.('idle')
          return
        }

        if (!this.opened && this.isAuthClose(event)) {
          this.options.onStatusChange?.('unavailable')
          this.options.onError?.(
            new Error(SOFTPHONE_REALTIME_AUTH_ERROR_MESSAGE)
          )
          return
        }

        this.options.onStatusChange?.('disconnected')
        this.scheduleReconnect()
      }
    } catch (error) {
      if (generation !== this.connectGeneration || this.intentionallyClosed)
        return
      this.options.onStatusChange?.('error')
      this.options.onError?.(error as Error)
      this.scheduleReconnect()
    }
  }

  close(): void {
    this.intentionallyClosed = true
    this.connectGeneration += 1
    this.clearReconnectTimer()
    this.socket?.close()
    this.socket = null
    this.options.onStatusChange?.('idle')
  }

  private registerDispatcher(): void {
    if (
      !this.options.dispatcherId ||
      this.socket?.readyState !== WebSocket.OPEN
    ) {
      return
    }

    this.socket.send(
      JSON.stringify({
        message: 'softphone',
        data: JSON.stringify({
          action: 'register',
          entity: 'dispatcher',
          user_id: this.options.dispatcherId,
        }),
      })
    )
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer()
    const maxAttempts =
      this.options.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS

    if (this.reconnectAttempt >= maxAttempts) {
      this.options.onStatusChange?.('unavailable')
      this.options.onError?.(
        new Error('Softphone realtime reconnect attempts exhausted')
      )
      return
    }

    const delay = Math.min(30000, 1000 * 2 ** this.reconnectAttempt)
    this.reconnectAttempt += 1

    this.reconnectTimer = window.setTimeout(() => {
      this.connect()
    }, delay)
  }

  private isAuthClose(event: CloseEvent): boolean {
    return event.code === 1008 || event.code === 4401 || event.code === 4403
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) return
    window.clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
  }
}
